import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import ContainedButton from "Components/HomeV2/Buttons/Contained";
import { useState } from "react";
import aiText from "../../Assets/AIOnDemand/AI-text.png";
const questions = [
    {
        question: "Your core offer, services or products",
        placeholder: "What products or services do you provide to your customers?"
    },
    {
        question: "Who is your ideal client profile?",
        placeholder: "Describe your target customer demographic"
    },
    {
        question: "What is your annual revenue?",
        placeholder: "Enter your current annual revenue"
    },
    {
        question: "What is your target revenue goal in 1 Year from now?",
        placeholder: "Enter your revenue target for next year"
    },
    {
        question: "Please provide the link to your website.",
        placeholder: "https://salesmrkt.com"
    },
    {
        question: "Please provide your email",
        placeholder: "john@salesmrkt.com"
    }
];

const AuditForm = () => {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState<string[]>(Array(questions.length).fill(""))
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(false);
    const [validationError, setValidationError] = useState("");

    const progress = Math.min(100, Math.round(((step) / (questions.length - 2)) * 100));

    const validateInput = (value: string, index: number): boolean => {
        if (!value || value.trim().length < 3) {
            setValidationError("Please enter at least 3 characters.");
            return false;
        }
        const questionText = questions[index].question.toLowerCase();

        if (questionText.includes("email")) {
            const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
            setValidationError(isValid ? "" : "Please enter a valid email address.");
            return isValid;
        }
        if (questionText.includes("website")) {
            const isValid = /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*$/.test(value.trim());
            setValidationError(isValid ? "" : "Please enter a valid website address (e.g., salesmrkt.com). Do not include http(s) or www.");
            return isValid;
        }
        if (questionText.includes("linkedin")) {
            const isValid = /^https?:\/\/(www\.)?linkedin\.com\/.+/.test(value);
            setValidationError(isValid ? "" : "Please enter a valid LinkedIn profile URL.");
            return isValid;
        }

        setValidationError("");
        return true;
    };

    const handleNext = async () => {
        if (!validateInput(answers[step], step)) return;

        if (step < questions.length - 1) {
            setStep(step + 1);
            setValidationError("");
        } else {
            setLoading(true);
            setError(false);
            try {
                const response = await fetch(process.env.REACT_APP_AUDIT_REPORT_ENDPOINT as string, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        data: questions.map((q, idx) => ({
                            question: q.question,
                            answer: answers[idx]
                        }))
                    })
                });
                if (response.ok) {
                    setSubmitted(true);
                } else {
                    setError(true);
                }
            } catch {
                setError(true);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const updatedAnswers = [...answers];
        updatedAnswers[step] = e.target.value;
        setAnswers(updatedAnswers);
        validateInput(e.target.value, step);
    };

    return (
        <section className="bg-black py-16 mb-10 flex flex-col items-center justify-center px-4 text-white">
            {submitted ? (
                <div className="text-center">
                    <h1 className="text-3xl md:flex-row flex-col md:text-6xl font-normal text-white mb-6 flex justify-center items-center p-6 gap-2">
                        Your <p className='text-brand-green'>Growth Strategy Report </p> is on it's way!
                    </h1>
                    <p className="mb-4 text-center text-sm md:text-xl font-light transition-opacity duration-500 w-full opacity-100">
                        Please check your email within an hour to get your report.
                    </p>
                    <DotLottieReact
                        src="https://lottie.host/3a05d072-0659-4378-a85d-381a6ab125e6/CBeI1ChFOz.lottie"
                        loop
                        autoplay
                        className='w-full max-w-[600px] mx-auto'
                    />
                </div>
            ) : (
                <>
                    <h1 className="text-3xl md:text-6xl whitespace-nowrap font-normal text-white mb-6 flex-col md:flex-row flex justify-center items-center p-6 gap-2">
                        Get Your Free
                        <div className="flex-row gap-2 hidden md:flex">
                            <img src={aiText} alt="AI" className="h-full w-auto object-contain max-h-10 md:max-h-16 ml-2 mr-1" />
                            Pipeline Audit
                        </div>
                        <div className='flex-row gap-2 flex md:hidden'>
                            <img src={aiText} alt="AI" className="h-full w-auto object-contain max-h-10 md:max-h-16 ml-2 mr-1" />
                            Pipeline Audit
                        </div>
                    </h1>
                    <p className="mb-4 text-center text-sm md:text-xl font-light transition-opacity duration-500 opacity-100">
                        {questions[step].question}
                    </p>
                    <textarea
                        value={answers[step]}
                        onChange={handleChange}
                        placeholder={questions[step].placeholder}
                        className="w-full max-w-xl p-4 rounded-md text-white bg-gray-500/30 border border-white text-sm md:text-base transition-opacity duration-500 opacity-100"
                        rows={4}
                    />
                    {validationError && (
                        <div className="text-red-500 text-sm mt-2 w-full max-w-xl">{validationError}</div>
                    )}
                    <div className="w-full max-w-xl mt-6">
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-brand-green transition-all duration-300"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <div className="text-right text-xs mt-1">{progress}%</div>
                    </div>
                    {error && <div className="text-red-500 mt-2">Something went wrong. Please try again later.</div>}
                    <div className='flex flex-row justify-between w-full max-w-xl'>
                        <div className="w-fit mt-5" onClick={() => setStep(step - 1)}>
                            <button
                                disabled={step === 0}
                                className={`${step === 0 ? "opacity-50 pointer-events-none" : "hover:bg-opacity-70 transition-all duration-300 ease-in-out"}font-medium rounded-full group flex items-center justify-center overflow-hidden w-full md:w-max bg-gray-300  py-2 px-6 text-base md:px-8 md:text-lg md:py-2.5 `}>
                                <span className="flex items-center gap-2 text-black px-12">
                                    ← Back
                                </span>
                            </button>
                        </div>
                        <div className="w-fit  mt-5" onClick={handleNext}>
                            <ContainedButton theme="primary">
                                <span className="flex items-center gap-2 text-black px-12">
                                    {loading ? 'Submitting...' : step === questions.length - 1 ? 'Submit' : 'Next →'}
                                </span>
                            </ContainedButton>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default AuditForm;
